import React from 'react';
import japanese from 'japanese';
import { connect } from 'react-redux';
import { Field, getFormValues, change as changeForm } from 'redux-form';
import { AlertTriangle } from 'jbc-front/components/icons';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { SAVE_THEN_MOVE_TO_EDIT, SAVE_THEN_BACK_TO_LIST } from 'src/constants/Generals';
import { CLIENT_PAYROLL_RULES_GROUP_LIST } from 'src/constants/EndpointUrls';
import { EMPLOYEE_FORM } from 'src/constants/FormNames';
import { DateField, TextField, Section, DoubleTextField, SelectField } from 'src/jbc-front/components/Form';
import Button from 'jbc-front/components/Button';
import ActionButton from 'jbc-front/components/ActionButton';
import * as CssClassNames from 'src/constants/CssClassNames';
import { isDateStringFormat, isEmail, isStaffCode, isPersonalNumber } from 'src/utils/CustomValidators';
import { detectButton } from 'src/utils/Form';
import { getSelectOptions } from 'src/reducers/selectOptions';
import selector from 'src/utils/Utils';
import { ErrorsSection } from 'src/components';
import isUseOtherApp from 'src/utils/JbcId';
import PayrollRuleInfo from './PayrollRuleInfo';
import styles from './EmployeeCreatingForm.scss';

const EmployeeCreatingForm = props => {
  const {
    handleSubmit,
    onSubmit,
    pristine,
    errors,
    submitting,
    payrollRulesGroups,
    selectedPayrollRulesGroupId,
    anyJobRunning,
    anyApiRunning,
    isTutorialRunning
  } = props;
  const { t, i18n } = useTranslation();
  const isVietnamese = i18n.language === 'vi';

  return (
    <form className={classNames(CssClassNames.FORM, CssClassNames.EMPLOYEE_FORM)} onSubmit={handleSubmit}>
      <Section title={t('features.Employees.showEmployee.basic_information')}>
        <ErrorsSection errors={errors} />

        <DateField
          required
          label={t('features.EmployeeBonus.components.EmployeeBonusTable.start_date')}
          viewMode="months"
          name="joinedOn"
          validate={isDateStringFormat}
        />

        <DoubleTextField
          t={t}
          isReverse={isVietnamese}
          fields={[
            { name: 'firstName', label: t('features.Employees.components.BasicInfoSection.first_name'), fieldProps: { type: 'text', maxLength: 32 } },
            { name: 'lastName', label: t('features.Employees.components.BasicInfoSection.last_name'), fieldProps: { type: 'text', maxLength: 32 } }
          ]}
        />

        <DateField
          label={t('features.Employees.components.BasicInfoSection.birth_date')}
          viewMode="years"
          name="birthday"
          validate={isDateStringFormat}
        />

        <TextField
          label={t('features.Employees.components.BasicInfoSection.email')}
          type="text"
          name="email"
          validate={isEmail}
          maxLength="255"
        />

        <TextField
          label={t('features.Employees.components.BusinessInfoSection.staff_code')}
          type="text"
          name="staffCode"
          maxLength="50"
          validate={isStaffCode}
          required
        />

        <TextField
          label={t('features.Employees.components.EmployeeCreatingForm.personal_id')}
          type="text"
          name="personalNumber"
          maxLength="13"
          validate={isPersonalNumber}
          required
        />

        <SelectField
          label={t('features.Employees.InsertBatchCsv.salary_rule_group')}
          required
          options={payrollRulesGroups}
          name="clientPayrollRulesGroupId"
        />
        <ActionButton as="a" href={CLIENT_PAYROLL_RULES_GROUP_LIST} target="_blank">
          {t('features.Employees.components.EmployeeCreatingForm.go_to_salary_rule_group_addition_page')}
        </ActionButton>

        <PayrollRuleInfo payrollRule={payrollRulesGroups.find(item => item.id === selectedPayrollRulesGroupId)} />

        <Field type="hidden" component="input" name="baseSalary" />
      </Section>
      <div className="u-ta-c u-mt30">
        {isUseOtherApp && (
          <div className={styles.cation}>
            <AlertTriangle size={35} />
            <p>
              {t('features.Employees.components.EmployeeCreatingForm.When_the_employee_is_already_registered_in_other_JOBCAN_services')}
              <br />
              {t('features.Employees.components.EmployeeCreatingForm.Name_and_staff_code_will_be_automatically_reflected_in_other_JOBCAN_services')}
            </p>
          </div>
        )}
        <Button
          primary
          disabled={submitting || pristine || anyJobRunning || anyApiRunning || isTutorialRunning}
          onClick={handleSubmit(detectButton(onSubmit, SAVE_THEN_BACK_TO_LIST))}
          className="u-mr20"
        >
          {t('features.Employees.components.EmployeeCreatingForm.save_and_return_to_the_list')}
        </Button>
        <Button
          primary
          disabled={submitting || pristine || anyJobRunning || anyApiRunning}
          onClick={handleSubmit(detectButton(onSubmit, SAVE_THEN_MOVE_TO_EDIT))}
        >
          {t('features.Employees.components.EmployeeCreatingForm.save_and_enter_detailed_information')}
        </Button>
      </div>
    </form>
  );
};

const mapStateToProps = state => ({
  payrollRulesGroups: getSelectOptions(state, 'payrollRulesGroups'),
  titles: getSelectOptions(state, 'titles'),
  selectedPayrollRulesGroupId: selector(getFormValues(EMPLOYEE_FORM)(state), 'clientPayrollRulesGroupId')
});

export default connect(mapStateToProps)(EmployeeCreatingForm);
